import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import config from "../config.json";
import toast from "react-hot-toast";

const StartReport = () => {
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const mail = params.get("mail");
  const url = params.get("url");
  const totalScore = params.get("totalScore");
  const stressLevel = params.get("stressLevel");
  const gifUrl = params.get("gifUrl");

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("sm"), {
    noSsr: true,
    defaultMatches: true,
  });

  const [countdown, setCountdown] = useState(-1);
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCountdown((prevCountdown) =>
        prevCountdown > 0 ? prevCountdown - 1 : prevCountdown
      );
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateUrl = (url) => {
    try {
      new URL(url);
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = "http://" + url;
      }
      return true;
    } catch (_) {
      return false;
    }
  };

  const validateInputs = (email, url) => {
    const isEmailValid = validateEmail(email);
    const isUrlValid = validateUrl(url);

    return { isEmailValid, isUrlValid };
  };

  const { isEmailValid, isUrlValid } = validateInputs(mail, url);

  useEffect(() => {
    if (!isEmailValid) {
      toast.error("Invalid email");
    }
    if (!isUrlValid) {
      toast.error("Invalid URL");
    }
    if (isEmailValid && isUrlValid) {
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete("mail");
      newUrl.searchParams.delete("url");
      window.history.replaceState({}, document.title, newUrl.toString());
      startAudit(url, mail);
    }
  }, []);

  const formatTime = (seconds) => {
    if (seconds === -1) {
      return "--:--";
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const reportPolling = (id) => {
    setCountdown(4 * 60);
    let pollIntervals = [60000, 60000, 60000];
    let pollIndex = 1;

    const poll = () => {
      if (pollIndex >= 6) {
        setWaiting(true);
      }
      axios
        .get(`${config.apiBaseUrl}/reports/find?report_id=` + id)
        .then((response) => {
          if (response.data.report?.status === "complete") {
            toast.success("Report generated successfully");
            setWaiting(false);
            setTimeout(() => {
              window.location.href = "/audit/report/" + id;
            }, 1000);
          } else if (response.data.report?.status === "fail") {
            setWaiting(false);
            toast.error("Error generating report");
          } else {
            let interval = pollIntervals[pollIndex] || 20000;
            pollIndex = Math.min(pollIndex + 1, pollIntervals.length - 1);
            setTimeout(() => poll(), interval);
          }
        })
        .catch((error) => {
          setWaiting(false);
          console.error("Error fetching report status:", error);
          toast.error("Error fetching report status");
        });
    };

    setTimeout(() => poll(), pollIntervals[0]);
  };

  const startAudit = (url, mail = "") => {
    console.log("Starting audit for", url);
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "http://" + url;
    }
    axios
      .post(`${config.apiBaseUrl}/init-audit`, {
        "e-mail": config.milkTubAdminEmail,
        url: url,
        metadata: {
          client_mail: mail,
        },
      })
      .then((response) => {
        reportPolling(response.data.report?.report_id);
        toast.success("Audit started successfully");
      })
      .catch((error) => {
        console.error("Error starting audit:", error);
        toast.error("Error starting audit");
      });
  };

  if (!isUrlValid) {
    return (
      <Container>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            height: "100vh",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            Invalid URL
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ mt: 4, fontSize: { xs: "1.3rem", sm: "2rem" } }}
        >
          Success! Your Stress Score: {totalScore}
        </Typography>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontSize: { xs: "1rem", sm: "2rem" } }}
        >
          Your Stress Level: <strong>{stressLevel}</strong>
        </Typography>
        <Box
          sx={{ display: "flex", justifyContent: "center", maxWidth: "100%" }}
        >
          <img
            src={gifUrl}
            alt="Stress Level GIF"
            style={{ maxWidth: "100%" }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: {xs:"4rem",sm:"3vh"},
            "& .MuiCircularProgress-root": {
              width: { xs: "5rem !important", sm: "10rem !important" },
              height: { xs: "5rem !important", sm: "10rem !important" },
            },
          }}
        >
          <CircularProgress />
        </Box>
        {isLargeScreen ? (
          <>
            <Typography variant="h4" sx={{ mt: 2 }}>
              Your report is being generated
            </Typography>
            {waiting ? (
              <Typography variant="h6" textAlign={"center"}>
                Taking longer than expected <br />
                please wait...
              </Typography>
            ) : (
              <Typography variant="h6">
                Estimated time remaining:{" "}
                <span id="countdown">{formatTime(countdown)}</span> minutes
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Your report is being generated
            </Typography>
            {waiting ? (
              <Typography variant="body1" textAlign={"center"}>
                Taking longer than expected <br />
                please wait...
              </Typography>
            ) : (
              <Typography variant="body1">
                Estimated time remaining:{" "}
                <span id="countdown">{formatTime(countdown)}</span> minutes
              </Typography>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default StartReport;
